import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import React from 'react';
import PropTypes from 'prop-types';
import createThemedComponent from '../../../theming/utils/createThemedComponent';
import { FacebookIcon } from './FacebookShareButton.styles';
import IconButton from '../IconButton';
import useFacebookShare from '../../hooks/useFacebookShare';
/**
 * Themeable FacebookShareButton component which loads the Facebook SDK automatically and provides facebook sharing functionality with the click of a button.
 *
 * By default, this button logs 3 possible piwik events:
 *
 * - `event(['share', 'facebook-start', <PAGE_BEING_SHARED>]);`
 *    - A user starts the share action
 * - `event(['share', 'facebook-success', <PAGE_BEING_SHARED>]);`
 *    - A user succesfully shared to facebook
 * - `event(['share', 'facebook-failure', <PAGE_BEING_SHARED>]);`
 *    - A user either cancelled their share or the share failed
 *
 * If choosing to continue with the default piwik events provided, a value must be provided for the `page` prop.
 *
 * Alternatively, if an object is passed for the `piwikEvents` props, then 3 custom piwik events will be logged instead of the default options. In this case, the `page` prop does not need to have a value passed.
 *
 * If choosing to do so, `piwikEvents` must be passed in as such: `{start: [], success:[], failure:[]}`
 *
 *```js
 * import { FacebookShareButton } from '@minerva-analytics/pantheon';
 *
 * // Without custom piwik events
 * <FacebookShareButton
 *   url="https://google.com"
 *   description="Check out this new website I just found!"
 *   page="example-page"
 *   appId="12345"
 * />
 *
 * // With custom piwik events
 * <FacebookShareButton
 *   url="https://google.com"
 *   description="Check out this new website I just found!"
 *   appId="12345"
 *   piwikEvents={start:['click','start'], success:['click','success'], failure:['click','failure']}
 * />
 *```
 *
 * **Default Styles**
 *
 *To view the `FacebookShareButton` styling directly, see [FacebookShareButton.styles.js](https://git.cogolo.net/minerva/pantheon/blob/master/src/components/widgets/FacebookShareButton/FacebookShareButton.styles.js)
 *
 **/

export var FacebookShareButtonComponent = function FacebookShareButtonComponent(_ref) {
  var url = _ref.url,
      description = _ref.description,
      page = _ref.page,
      appId = _ref.appId,
      piwikEvents = _ref.piwikEvents,
      rest = _objectWithoutPropertiesLoose(_ref, ["url", "description", "page", "appId", "piwikEvents"]);

  var share = useFacebookShare({
    url: url,
    description: description,
    page: page,
    appId: appId,
    piwikEvents: piwikEvents
  });
  return /*#__PURE__*/React.createElement(IconButton, _extends({
    onClick: function onClick() {
      return share();
    }
  }, rest), /*#__PURE__*/React.createElement(FacebookIcon, null));
};
FacebookShareButtonComponent.defaultProps = {
  piwikEvents: {
    start: ['share', 'facebook-start', '<PAGE_BEING_SHARED>'],
    success: ['share', 'facebook-success', '<PAGE_BEING_SHARED>'],
    failure: ['share', 'facebook-failure', '<PAGE_BEING_SHARED>']
  }
};
FacebookShareButtonComponent.propTypes = {
  /** URL that is being shared */
  url: PropTypes.string.isRequired,

  /** Custom description for the URL */
  description: PropTypes.string,

  /** The page that this component is being used on */
  page: PropTypes.string,

  /** Facebook app ID */
  appId: PropTypes.string.isRequired,

  /** Optional object consisting of 3 custom events: start, success, failure. Object keys must be `start`, `success` and `failure` with values as arrays in the form of [category, action, name, value], though name and value are optional. */
  piwikEvents: PropTypes.object
};
var FacebookShareButton = createThemedComponent('FacebookShareButton', FacebookShareButtonComponent);
export default FacebookShareButton;